/**
 * What is LEFT of the front's read-model glue (MYAC-930) once schema v2 landed.
 *
 * The adapter layer is gone: §12.25 made the views speak the front's component
 * vocabulary, so `RatingBadges` and `ThemesBand` consume view fields DIRECTLY
 * (`toRatings` / `toThemeSummary` deleted), and item rows carry `order`, so the
 * client no longer sorts clause codes (`compareItemCodes` deleted).
 *
 * What remains is of two kinds, and neither is a shape adapter:
 *  - **Lens rules** (`lensFor`, `effectiveRating`, `lensAnswerMd`) — display
 *    semantics of the declared stage, which the read-model exposes as data
 *    (`rating_stage1/2`, `answer_stage1/2_md`) precisely so the client can pick.
 *  - **Mappings into the REST-era DTOs the plan/summary components still take**
 *    (`toRequirementItemView`, `toVerdictView`, `toRemediationWithRef`) — they
 *    disappear when those components become view-native, not before.
 *
 * Two hard lines still hold (spec §4.0): **no aggregation** — not one function
 * here counts, sums or scores; and **no structure derivation** — the chapter/sub
 * tree and `sub_key` join keys come from the nav doc. Ordering of the STACKED
 * STANDARDS is the one thing left to the client, because it is pure display.
 */

import type { ViewRow } from '@/composables/useClientViews'
import { PHASE_ORDER } from '@/models/assessmentViews'
import { isoFromTimestamp } from '@/utils/date'
import type {
  ActiveUnit,
  AssessmentItemRow,
  AssessmentPhase,
  AssessmentRemediationRow,
  AwaitKind,
  HistoryNote,
  NavAdoption,
  RunRow,
  RunRowState,
  ViewLens,
  AssessmentTaskGroupsView,
} from '@/models/assessmentViews'
import type {
  AssessmentVerdictView,
  RequirementItemView,
  ScopeTrack,
} from '@/models/requirementsAssessment'
import type { RemediationWithRef } from '@/utils/actionPlan'
import type { ProjectTask } from '@/models/ProjectTask'
import { taskNeverAccepted } from '@/models/taskLifecycle'
import { isPriorityKey, TASK_PRIORITY_TO_KEY } from '@/components/shared/priority/tokens'

/** An item row paired with the referential it belongs to (plan-tab joins). */
export type ItemViewWithRef = RequirementItemView & { referentialId: string }

/**
 * The display lens of one adoption: TD always reads the combined (stage-2)
 * projection; a QMS standard reads the stage it declared.
 */
/**
 * The ONE credit estimate key every assessment gate prices with, QMS and TD alike
 * (MYAC-1469), times the number of requirements the run assesses. The backend's
 * admission gate prices the same key and the same count (`requirement_assessment` in
 * `CreditService`), so a launch the front lets through is one the engine accepts.
 *
 * It replaced a per-track pair (`assessment_per_clause` on QMS,
 * `td_assessment_per_section` on TD) whose TD unit the launch dialog counted as one
 * per referential while the engine counted sections.
 */
export const ASSESSMENT_CREDIT_ACTION = 'assessment_per_requirement'

/**
 * The confirm popover's title key for a referential-level launch.
 *
 * A first run is «Évaluer {name} ?», not «Ré-évaluer {name} ?» (review H5): the
 * launch after an adoption is the FIRST assessment, and calling it a re-run
 * reads as "you already paid for this once".
 *
 * First launch is ONE predicate, projected by the engine (MYAC-1433):
 * `never_launched` on the nav row — no verdict AND no run ever opened for the
 * adoption. The front used to re-derive it twice from counts and the run state
 * (`neverAssessedScope`, `neverLaunched`, MYAC-1211 / MYAC-1362); both are gone.
 * Absent on an older projection ⇒ a re-assessment title, never a false
 * "first run".
 */
export function reassessTitleKey(std: { never_launched?: boolean }): string {
  return std.never_launched === true
    ? 'referentials.reassessConfirm.titleFirstRun'
    : 'referentials.reassessConfirm.titleReferential'
}

/**
 * How many requirements an ADOPTION holds — its live items, N/A and waiting included
 * (MYAC-1589; the projection partitions them as `assessable_total + na + waiting`):
 * what the Manage dialog shows on an adopted row, which may differ from the catalogue's
 * newer revision. 0 means not projected yet (an empty pack, an older projection without
 * the denominator), as `ratedOf` reads it: undefined, so the row falls back to the
 * catalogue's count rather than printing « 0 requirements ».
 */
export function adoptionRequirementCount(std: {
  assessable_total?: number
  na?: number
  waiting?: number
}): number | undefined {
  const total = (std.assessable_total ?? 0) + (std.na ?? 0) + (std.waiting ?? 0)
  return total > 0 ? total : undefined
}

/**
 * How many requirements a referential-level launch covers — the credit gate's
 * unit count.
 *
 * The banner used to pass a hardcoded `1`, which sized the gate for one clause
 * on a launch that assesses the whole standard (review H5). `1` survives only as
 * the tolerant-reader fallback for a projection that predates `assessable_total`
 * — never `0`, which would wave every launch through the gate.
 */
export function reassessScopeCount(std: { assessable_total?: number }): number {
  return std.assessable_total ?? 1
}

/**
 * Does this pending clarification bear on a launch of `adoptionId` on `track`?
 *
 * A row naming an adoption bears on that adoption only. A scope-level question
 * (`adoption_id` empty) is a fact about the PRODUCT; `scope_track` is the addressing
 * field and is always set. Which referentials of the track it bears on (IR-165):
 * - a REGISTRY question (it carries a profile `field`) bears on the referentials
 *   whose plan gates on that key (`gateKeys`, the adoption's nav `gate_keys`) and
 *   on the one that asked it (`raised_by`) — ISO 20417's repackaging question no
 *   longer holds up IEC 62366-1;
 * - a FREE question (no `field`), or a referential whose gate keys are UNKNOWN
 *   (`null`/absent: never planned, plan stamp reset by a relaunch, older
 *   projection), keeps the broad rule — every referential of the track ran on the
 *   conservative reading of it. An EMPTY list is a real answer (the plan gates on
 *   no key): a registry question then bears only on its asker.
 *
 * ONE predicate, because the count on the launch button and the list inside the guard
 * dialog must never disagree: the list used to be track-blind and showed questions the
 * count had excluded.
 */
export function clarificationApplies(
  row: { adoption_id?: string; scope_track?: string; field?: string; raised_by?: string },
  adoptionId: string,
  track: string,
  gateKeys?: readonly string[] | null,
): boolean {
  if ((row.scope_track ?? '') !== track) return false
  if (row.adoption_id) return row.adoption_id === adoptionId
  if (!row.field || gateKeys == null) return true
  return gateKeys.includes(row.field) || row.raised_by === adoptionId
}

/**
 * The pending clarifications a launch of `adoptionId` on `track` is waiting on.
 *
 * ONE function feeds both the count on the launch button (`.length`) and the list
 * inside the guard dialog, and it runs on the RAW projected rows: the dialog used to
 * filter the Questions panel's display rows, which had dropped `scope_track`, so
 * `clarificationApplies` rejected every row — the button said « 2 questions », the
 * dialog listed none and offered a plain « Lancer » (app-dev, 2026-09-24).
 *
 * `gateKeys` — the adoption's nav `gate_keys` — narrows the registry questions to
 * the referentials they gate (see `clarificationApplies`); `null`/omitted ⇒ broad
 * rule, `[]` ⇒ registry questions bear only on their asker.
 */
export function guardQuestionsFor<
  T extends { adoption_id?: string; scope_track?: string; field?: string; raised_by?: string },
>(rows: readonly T[], adoptionId: string, track: string, gateKeys?: readonly string[] | null): T[] {
  return rows.filter((row) => clarificationApplies(row, adoptionId, track, gateKeys))
}

/**
 * Does the per-row «questions en attente» CHIP belong to `adoptionId`?
 *
 * Décision Charles 19/08: a scope-level question used to chip EVERY
 * referential of the track — the chip now follows the ASKER (`raised_by`,
 * stamped by the engine from the scope's busy adoption). The launch guard
 * keeps `clarificationApplies` (every referential the answer refines — the
 * whole track, or the gated ones for a registry question, IR-165); only the
 * attribution is per-row. A legacy row with no
 * raiser info keeps the broadcast (tolerant reader).
 */
export function clarificationChipApplies(
  row: { adoption_id?: string; scope_track?: string; raised_by?: string },
  adoptionId: string,
  track: string,
): boolean {
  if ((row.scope_track ?? '') !== track) return false
  if (row.adoption_id) return row.adoption_id === adoptionId
  if (row.raised_by) return row.raised_by === adoptionId
  return true
}

export function lensFor(track: ScopeTrack, mode: string | undefined): ViewLens {
  if (track === 'td') return 'stage2'
  return mode === 'stage2' ? 'stage2' : 'stage1'
}

/**
 * What the clause card shows as the letter: 'N/A' for a non-applicable item,
 * the lens projection when present, '?' when the item has no verdict yet.
 *
 * `rating` is already the effective-lens value the nav counters were built from;
 * `rating_stage1/2` exist so the card can render the *other* lens' gap lines
 * without a second aggregate (spec §4.0-2).
 */
export function effectiveRating(row: AssessmentItemRow, lens: ViewLens): string {
  if (row.status === 'not_applicable') return 'N/A'
  const projected = lens === 'stage2' ? row.rating_stage2 : row.rating_stage1
  return projected || row.rating || '?'
}

/**
 * The composed half of the answer for the active lens — one line per obligation
 * followed by the closing constat, markdown, ready to render under the rationale.
 *
 * It is COMPOSED SERVER-SIDE, from the same constat rows the letter derives from:
 * that is the only arrangement in which the prose and the grade cannot tell
 * different stories, and it gives every surface one source (this screen today, the
 * PDF export next). The client picks the lens; it does not rebuild the sentence —
 * the previous version derived gap lines here and drifted from the derivation the
 * moment the records barème landed.
 */
export function lensAnswerMd(row: AssessmentItemRow, lens: ViewLens): string {
  const v = row.verdict
  if (!v) return ''
  return (lens === 'stage2' ? v.answer_stage2_md : v.answer_stage1_md) ?? ''
}

/**
 * An item row in the shape the action-plan / summary dialogs expect.
 *
 * `identity` (the blackboard row key) is NOT in the read-model — the front
 * substitutes `requirement_uid`, which is the only stable item key views carry;
 * `grounding_seen` has no view field at all (no screen renders it).
 */
export function toRequirementItemView(
  row: AssessmentItemRow,
  referentialId: string,
): ItemViewWithRef {
  return {
    requirement_uid: row.requirement_uid,
    identity: row.requirement_uid,
    code: row.code,
    title: row.title,
    description: row.description ?? '',
    status: row.status,
    applicability_justification: row.applicability_justification ?? '',
    grounded: row.grounded ?? false,
    grounding_seen: false,
    referentialId,
    adoption_id: row.adoption_id,
  }
}

/** An item row's current verdict in the shape the origin dialogs expect.
 *
 * `lens` is the ADOPTION's declared lens (`lensFor`), not the caller's UI
 * state: the composed answer is per-stage, and dropping it here was why the
 * origin dialogs rendered the thin criteria fallback while the Évaluation tab
 * showed the full answer — same row, same component, thinner DTO. */
export function toVerdictView(row: AssessmentItemRow, lens: ViewLens): AssessmentVerdictView {
  return {
    identity: row.requirement_uid,
    rating: row.rating,
    rationale: row.verdict?.rationale_md ?? '',
    answer_md: row.status === 'not_applicable' ? '' : lensAnswerMd(row, lens),
    rating_justification: row.verdict?.rating_justification ?? '',
    rating_stage1: row.rating_stage1,
    rating_stage2: row.rating_stage2,
    criteria_status: row.verdict?.criteria_status ?? [],
    stale: row.stale ?? false,
    // `section` is optional in the view (omitted when the excerpt is whole-doc).
    // `origin` rides through as-is (MYAC-1051 Task 10) — 'human' | '' | absent.
    evidence: (row.verdict?.evidence ?? []).map((e) => ({
      filename: e.filename,
      section: e.section ?? '',
      excerpt: e.excerpt,
      justification: e.justification,
      origin: e.origin,
      document_handle: e.document_handle,
    })),
  }
}

/**
 * A remediation row in the shape `ActionPlanPanel` consumes.
 *
 * Three fields need translating, and each is a contract note:
 *  - `description_md` → `description` (the view renamed it).
 *  - `theme` (4C facet) → `category`, because the legacy row renders its
 *    "consistency" chip off `category`; the row's `theme` slot takes `topic`,
 *    the ex-`theme` free-form content label the view renamed to end the
 *    collision (spec §4.6).
 *  - `identity` is gone from the read-model, so the first anchor's uid stands in
 *    (falling back to the row id). The plan dedups on it — two open actions on
 *    the same clause therefore count as one, which is exactly what the view's
 *    materialized `open_actions` count reproduces (§12.20).
 *
 * The anchors carry v2's `code`/`title` snapshots (§12.19) so an anchor chip
 * labels itself without an item read, and (MYAC-1272 review round 2) the
 * additive `label` snapshot — the display name for an anchor whose `code`
 * stays empty on purpose (a synthetic obligation, never a fabricated code).
 */
/**
 * Does this task own its row's review state? (4.8a — MYAC-1028.)
 *
 * **The front's single copy of CMS's `TASK_OWNS_REVIEW`**, and deliberately the only
 * one: this is already the third implementation of "is this accepted" in the
 * platform — the projection has one, the apply layer has one (MYAC-1019) — and only
 * those two are pinned to each other. Everything in the browser that needs the
 * question asks here.
 *
 * **Lifecycle alone does not answer it** (MYAC-1095). `active` and `archived` own the
 * review outright; `unapproved` SPLITS on why the task was sent back, and CMS's
 * `apply.task_owns_review` is the rule this mirrors case for case:
 *
 * | Task | Owns the row's review | Because |
 * | --- | --- | --- |
 * | `active` | yes | it is the accept |
 * | `archived` | yes | off the board, but it WAS decided — a row that forgot would offer the accept button again for a task that is right there |
 * | `unapproved` + `rejected` | **yes** | the rejection lives on the task and nowhere else: `task_unapprove` writes the ruling and deliberately skips the row (`views/**` has one writer), so a row reading its own entry would show undecided and throw the typed reason away |
 * | `unapproved` + `pending` | no | un-ACCEPTING hands the row back to its entry, and that fall-through is what puts the accept button back — the point of the state |
 * | `parked` + `rejected` | **yes** | MYAC-1639: set aside after a comment or an edit — the ruling went through the task, for the same reason as the sent-back rejection above |
 * | `parked` + `pending` | no | MYAC-1639: the hidden task of an action nobody has decided on — the row still reads "to decide" |
 *
 * The two sent-back outcomes are the crux, and reading them as one is the bug this
 * replaced: the front held the old `active || archived` copy while CMS had already
 * moved, so a rejected referential task rendered right and could not be undone.
 *
 * Absent reads as `active`: phase 0.2 added the field with that default and ran no
 * backfill, so most tasks carry nothing here. That is the same reading
 * `ProjectTask.isSettled` and `isOnBoard` already take, and CMS never sees the case
 * because its model defaults the field on load. An absent `review_decision` on a
 * sent-back task therefore reads as NOT rejected — a send-back with no ruling recorded is
 * the `pending` shape, which is also what `apply_unapprove`'s own default writes.
 *
 * Neither `isSettled` nor `isOnBoard` is this predicate — both exclude `archived`,
 * so reusing either would un-accept every archived card.
 */
export function taskOwnsRemediationReview(task: ProjectTask | null | undefined): boolean {
  // MYAC-1639 — retired straight from `parked`: never accepted, owns nothing.
  if (!task || taskNeverAccepted(task)) return false
  const lifecycle = task.lifecycle ?? 'active'
  if (lifecycle === 'active' || lifecycle === 'archived') return true
  return (
    (lifecycle === 'unapproved' || lifecycle === 'parked') && task.review_decision === 'rejected'
  )
}

/**
 * Is the owning task's ruling a REJECTION? (MYAC-1095.)
 *
 * Split out because two places need the same answer — what the row displays, and
 * which command its undo sends — and because `taskOwnsRemediationReview` above is
 * true for an accepted task and a rejected one alike.
 *
 * Read off the task rather than the row on purpose: for a remediation the task is
 * the only record of the rejection, which is the whole of MYAC-1095.
 */
export function taskRejectedRemediation(task: ProjectTask | null | undefined): boolean {
  return !!task && task.review_decision === 'rejected'
}

/**
 * Can this row's decision still be undone? (MYAC-1095.)
 *
 * `archived` is terminal in CMS's lifecycle, and the reason is now simply that it
 * appears in NO permitted set at all — `PERMITTED_LIFECYCLES` has no entry admitting
 * it, which is the whole of "archived is terminal". (It used to be phrased as
 * `task_unapprove` starting only from `active`; CMS `1316129b` widened that cell to
 * `{active, unapproved}`, which changes the reason and leaves the conclusion.) So a
 * row owned by an archived task has NO command in either direction — undo would be
 * refused POLICY every time. The control stays visible and disabled with its reason
 * (the disabled-over-hidden rule) rather than firing and failing.
 *
 * Every other case is undoable, and by ONE command: no owning task at all is the
 * pre-4.8 row whose decision lives on the entry and moves by `remediation_edit`;
 * `active` and `unapproved` alike un-approve to `pending`, which is what "Undo
 * decision" means on either row.
 */
export function canUndoRemediationReview(taskLifecycle: string | undefined): boolean {
  return (taskLifecycle ?? '') !== 'archived'
}

/** The three statuses the action-plan row's vocabulary has. */
const ROW_PROGRESS = ['todo', 'in_progress', 'done'] as const

/** The only two decisions the row's vocabulary has. */
const ROW_DECISION = ['accepted', 'rejected'] as const

/**
 * The row's stored decision, or `''` for anything outside its vocabulary.
 *
 * **This is load-bearing, not defensive tidying.** `apply_unapprove` writes
 * `review_decision: command.decision` onto the resolved `proposal_ref`, and for a
 * remediation task that ref IS this view row — so an un-approve stamps the row
 * with `pending`, a value `AssessmentRemediationRow` does not carry. `RemediationRow`
 * then branches `accepted` / else-rejected, so `pending` renders the row as
 * **rejected**: undo appeared to reject the item it had just un-accepted.
 *
 * The write itself is illegitimate — `views/**` has one writer — and removing it is
 * [MYAC-1035]. This narrowing is not waiting for that: the front should not render a
 * decision it has no word for, and after MYAC-1035 lands an old row may still carry
 * a `pending` written before the guard.
 *
 * Same tolerant-reader stance as the rest of the read-model: an unrecognised value
 * degrades to "undecided", which shows the accept/reject buttons — recoverable by a
 * click — rather than to a decision nobody made.
 */
function rowDecisionOf(row: ViewRow<AssessmentRemediationRow>): '' | 'accepted' | 'rejected' {
  const decision = row.review_decision ?? ''
  return (ROW_DECISION as readonly string[]).includes(decision)
    ? (decision as 'accepted' | 'rejected')
    : ''
}

/**
 * A task's progress in the ROW's vocabulary, or `''` where it has no word for it.
 *
 * The two vocabularies genuinely differ: `TaskProgressStatus` carries `cancelled`,
 * which `AssessmentRemediationRow.review_progress` does not, so a cancelled task has
 * no honest value here. Narrowed rather than widening the view contract — that
 * contract is the server's, and a client is not the place to add a state to it.
 *
 * `''` renders as `todo`, which is imperfect for a cancelled task; the alternative
 * is inventing a status the row cannot store. If cancelled ever needs to show on the
 * plan, it is a view-schema change and belongs on a card, not here.
 *
 * (`TaskProgressStatus` is a `Literal` rather than a `StrEnum` — flagged on
 * MYAC-1019 and again on MYAC-1029 — so this narrowing is also what keeps an
 * unrecognised server value from reaching a typed field.)
 */
function rowProgressOf(task: ProjectTask | null): '' | 'todo' | 'in_progress' | 'done' {
  const progress = task?.review_progress ?? ''
  return (ROW_PROGRESS as readonly string[]).includes(progress)
    ? (progress as 'todo' | 'in_progress' | 'done')
    : ''
}

/**
 * ...and, since 4.8a, the row's review state RESOLVED against its task.
 *
 * The task id IS the row id (§7.3): nothing writes a pointer either way, so the
 * shared id is the only join there is, and the browser already holds every task from
 * the Kanban store's single collection snapshot. The join therefore costs no reads.
 *
 * **Why here and not at the call sites.** There are two mapping sites — the Action
 * Plan (`ProductReferentialsView`) and the executive summary (`ExecutiveSummaryPanel`)
 * — and joining at either one closes the trap for that surface while reopening it for
 * the other. This is the funnel both go through.
 *
 * **`task` is REQUIRED, not optional.** An optional argument lets a future third call
 * site omit it and silently render undecided rows, which is exactly the failure this
 * exists to prevent. Same call the backend made in MYAC-1019, where `accepted_ids` is
 * required and keyword-only because "the default that would have to exist is the one
 * that closes the user's card." Pass `null` to mean *no task*, deliberately, rather
 * than by forgetting.
 *
 * **All four review fields move together**, exactly as CMS's `remediation_review`
 * moves them — leaving one behind was a real defect there (#339's second commit).
 * `review_progress` is the one that bites here: `apply_remediation_edit` still writes
 * progress onto the blackboard entry (MYAC-1029), so rows carry stale entry values,
 * and resolving only the decision leaves an accepted card showing the wrong status.
 *
 * **The resolved object mixes provenance on purpose — do not "fix" it.** A DERIVED
 * `review_decision` sits next to a STORED `last_revision_seq`, because they belong to
 * different commands: `remediation_edit` echoes the row's own token, while
 * `task_unapprove` / `task_edit` take theirs from `revisionSeqFor()`. Nothing sends
 * `review_decision` back in a command today, and it should stay that way.
 *
 * Resolving in place — rather than adding a `hasTask` flag every consumer would have
 * to learn — is what makes this row shape-identical to what a fixed projection would
 * emit. When the row is eventually derived where it is written, this join is a
 * deletion and nothing else changes.
 */
export function toRemediationWithRef(
  row: ViewRow<AssessmentRemediationRow>,
  referentialLabel: string,
  joined: ProjectTask | null,
  /**
   * MYAC-1639 — the content fields someone edited on the row's `parked` task
   * (`tasks.parkedEditsFor`). Accepting it keeps those and takes the row's
   * current text for every other field, so that is what the card shows. Absent
   * reads as none edited: the engine's text, which is what an accept would keep.
   */
  parkedEdits: ReadonlySet<string> = new Set(),
): RemediationWithRef {
  // MYAC-1639 — a hidden task retired before anyone added it is no task for its
  // row: no ownership, no link, and the row's own decision route stands.
  const task = taskNeverAccepted(joined) ? null : joined
  const taskOwns = taskOwnsRemediationReview(task)
  // Which ruling the owning task carries. `taskOwns` is true for an accepted task
  // and a rejected one alike, so the display needs this second question
  // (MYAC-1095) — the same split CMS's `remediation_review` makes.
  const taskRejected = taskOwns && taskRejectedRemediation(task)
  // MYAC-1639 — an undecided action's hidden task. The card shows what accepting
  // it will produce: the fields someone edited from the task, the rest from the
  // row as the engine has it now, and the task's assignee. The decision stays
  // the row's.
  const parked = task?.lifecycle === 'parked' ? task : null
  const editedOnTask = (field: string) => !!parked && parkedEdits.has(field)
  // MYAC-1659 — an added action's title and priority are its task's: they are
  // edited there, and the row keeps the engine's. A sent-back or set-aside
  // task's copy stays frozen behind the row, as the review shows it.
  const added = taskOwns && !taskRejected ? task : null
  const addedPriority = added?.priority
    ? isPriorityKey(added.priority)
      ? added.priority
      : TASK_PRIORITY_TO_KEY[added.priority]
    : undefined
  const anchors = (row.anchors ?? []).map((a) => ({
    identity: a.requirement_uid,
    requirement_uid: a.requirement_uid,
    code: a.code ?? '',
    title: a.title ?? '',
    label: a.label ?? '',
  }))
  return {
    id: row.id,
    identity: anchors[0]?.requirement_uid ?? row.id,
    anchors: anchors.length > 0 ? anchors : undefined,
    stage: row.stage ?? '',
    name: added?.name || (editedOnTask('name') && parked?.name) || row.name,
    description: editedOnTask('description')
      ? (parked?.description ?? '')
      : (row.description_md ?? ''),
    risk: editedOnTask('context.risk') ? (parked?.context?.risk ?? '') : (row.risk ?? ''),
    constats: editedOnTask('context.finding')
      ? [parked?.context?.finding ?? ''].filter(Boolean)
      : (row.constats ?? []),
    document_actions: row.document_actions ?? [],
    priority:
      addedPriority ?? (editedOnTask('priority') ? (parked?.priority ?? '') : (row.priority ?? '')),
    category: row.theme ?? '',
    theme: row.topic ?? '',
    documents: row.documents ?? [],
    status: row.status,
    referentialId: row.referential_id,
    referentialLabel,
    workGroup: row.group_label ?? '',
    // The board task this row was accepted into, for the link back to it
    // (MYAC-1079). Taken off the task document rather than rebuilt from the row
    // id: the two are the same string under §7.3, and only one of them stays
    // right if that ever changes.
    projectTaskRef: task?.path || undefined,
    // The task's own documents, with their done flags (MYAC-1084). Only the
    // task has them: `documents_from_actions` groups the row's actions by
    // (document, action_type) at accept time, so the row's own list cannot
    // stand in for this one — different length, different order.
    documentsV2: parked && !editedOnTask('documents_v2') ? undefined : task?.documentsV2,
    // P5b — the review state the row's actions read, and the two fields the
    // `remediation_edit` intent payload is built from (adoption + OCC token).
    //
    // 4.8a: the four review fields are RESOLVED against the task where one owns
    // them, and fall through to the row otherwise. Falling through is not
    // "undecided" — a row rejected and later accepted returns to `rejected`, and
    // every acceptance made before 4.8 is stamped on the row itself.
    adoption_id: row.adoption_id,
    // The task's own ruling, in the row's vocabulary — never hard-coded (MYAC-1095).
    // `accepted` covers every owning task that is not a rejection, including one
    // carrying no decision at all: an owning task exists, so the accept button must
    // not come back. `pending` is a word the row does not speak.
    review_decision: taskOwns ? (taskRejected ? 'rejected' : 'accepted') : rowDecisionOf(row),
    review_progress: taskOwns ? rowProgressOf(task) : (row.review_progress ?? ''),
    review_assignee_uid:
      taskOwns || parked
        ? (task?.review_assignee_uid ?? '')
        : (row.review_assignee_uid ?? ''),
    // The REJECT path's field. Carried from the TASK when the task's ruling is a
    // rejection — for a remediation nothing else holds it, since `task_unapprove`
    // skips the row write-back — and blank otherwise: an accepted item has no
    // rejection to explain, and the row's own comment is from the decision this one
    // replaced. CMS's projection makes exactly this split.
    review_rejection_comment: taskOwns
      ? taskRejected
        ? (task?.review_rejection_comment ?? '')
        : ''
      : (row.review_rejection_comment ?? ''),
    // MYAC-1639 — which reason was picked, from the same record as the comment.
    // Rows rejected before it carry none and show their comment alone.
    review_rejection_reason: taskOwns
      ? taskRejected
        ? (task?.review_rejection_reason ?? '')
        : ''
      : rowDecisionOf(row) === 'rejected'
        ? (row.review_rejection_reason ?? '')
        : '',
    // MYAC-1089 — the rejection's end, taken exactly as the comment above is:
    // off the task when the task's ruling is the rejection (`task_unapprove`
    // never writes the row back), off the row otherwise. '' = no end.
    review_rejected_until: taskOwns
      ? taskRejected
        ? isoFromTimestamp(task?.review_rejected_until)
        : ''
      : rowDecisionOf(row) === 'rejected'
        ? (row.review_rejected_until ?? '')
        : '',
    // ...and the note a rejection leaves when its period ran out, on a row back
    // to review. The projection carries it on the row for both routes; the
    // task's own copy covers the moment before the row is rebuilt.
    review_returned:
      !taskOwns && rowDecisionOf(row) === ''
        ? (row.review_returned ?? task?.review_returned ?? null)
        : null,
    // The owning task's lifecycle, for the one gate the row cannot derive from the
    // decision alone: `archived` is terminal, so its undo has no command to send.
    // '' when no task owns the row — that row's undo is a `remediation_edit` and
    // always available.
    taskLifecycle: taskOwns ? (task?.lifecycle ?? 'active') : '',
    taskParked: !!parked,
    // STORED, deliberately, next to the derived fields above — the entry's OCC
    // token, which only `remediation_edit` uses. See the mixed-provenance note.
    last_revision_seq: row.last_revision_seq ?? 0,
    finding_label: row.finding_label ?? '',
    proposed_change: row.proposed_change?.kind ? row.proposed_change : undefined,
  }
}

/**
 * A rejection the user has made that the row has not caught up with yet (MYAC-1512).
 *
 * Rejecting is a queued command and the row only changes once the apply layer has
 * run and the view has been rebuilt — seconds on a good day, and the reported ~15 s
 * on a bad one. Until then the row kept offering accept/reject, so the same action
 * could be rejected again and again. This is the optimistic half: the store records
 * the gesture the moment it is made, and `withPendingRejection` lays it over the
 * row so every reader of `review_decision` sees it without learning a new field.
 */
export interface PendingRejection {
  comment: string
  /** MYAC-1639 — the reason picked, shown with the rejection at once; absent = none. */
  reason?: string
  /** MYAC-1089 — the period's end as ISO-8601; '' or absent = no end. */
  rejectedUntil?: string
  /** `null` while the command is still being sent; the clock starts when it is accepted. */
  settledAt: number | null
}

/**
 * How long an accepted rejection may outlive the command before the row is trusted
 * again. Long enough for a slow rebuild, short enough that a rejection the server
 * silently never applied does not stay painted over the truth.
 */
export const PENDING_REJECTION_TTL_MS = 20_000

export function pendingRejectionLive(
  pending: PendingRejection | undefined,
  now: number = Date.now(),
): pending is PendingRejection {
  return !!pending && (pending.settledAt === null || now - pending.settledAt < PENDING_REJECTION_TTL_MS)
}

/**
 * Lay a pending rejection over a mapped row. A no-op once the row says `rejected`
 * itself, so the overlay never has to be taken off for the screen to be right.
 */
export function withPendingRejection(
  row: RemediationWithRef,
  pending: PendingRejection | undefined,
): RemediationWithRef {
  if (!pendingRejectionLive(pending) || row.review_decision === 'rejected') return row
  return {
    ...row,
    review_decision: 'rejected',
    review_rejection_comment: pending.comment,
    review_rejection_reason: pending.reason ?? '',
    review_rejected_until: pending.rejectedUntil ?? '',
    review_returned: null,
    rejectPending: pending.settledAt === null,
  }
}

/**
 * Display order of the stacked standards: QMS leads with its two anchor
 * referentials (ISO 13485, then EU MDR), everything else — and all of TD — is
 * A→Z on the name the view already provides.
 */
export function standardRank(referentialId: string, track: ScopeTrack): number {
  if (track !== 'qms') return 0
  const id = referentialId.toLowerCase()
  if (id.includes('13485')) return 0
  if (id.includes('mdr')) return 1
  return 2
}

export function sortNavAdoptions(adoptions: NavAdoption[], track: ScopeTrack): NavAdoption[] {
  return [...adoptions].sort(
    (a, b) =>
      standardRank(a.referential_id, track) - standardRank(b.referential_id, track) ||
      a.display_name.localeCompare(b.display_name, undefined, { numeric: true }),
  )
}

/**
 * Nothing on this track has been assessed yet (MYAC-1522): referentials are
 * adopted, and not one of them carries a verdict. It is what tells a section's
 * « nothing assessed yet » from its « assessed, nothing found »: « Aucune
 * action ouverte : rien à corriger » on a QMS adopted a minute ago was false.
 *
 * Read off the nav rows the page already holds, never a new read:
 * `by_rating.TOTAL` counts the rated requirements; a requirement found not
 * applicable is a judgement too, so `na` counts as assessed, except on a
 * referential nobody ever launched (`never_launched`), whose N/A can only come
 * from its plan. True during a first run until its first verdict lands: until
 * then nothing has been assessed. No rows (not read yet, nothing adopted)
 * claims nothing.
 */
export function nothingAssessedYet(rows: readonly NavAdoption[]): boolean {
  return (
    rows.length > 0 &&
    rows.every(
      (s) => (s.by_rating?.TOTAL ?? 0) === 0 && (s.never_launched === true || (s.na ?? 0) === 0),
    )
  )
}

// ---------------------------------------------------------------------------
// Where a run has got to, at one level of the tree (MYAC-969)
// ---------------------------------------------------------------------------

/**
 * The re-assess dot's vocabulary (« en cours / terminé dans ce lancement / à
 * venir / en attente / non applicable ») — the state of ONE requirement (or a
 * fold of them) within the track's LIVE run. Not a run state: the run's own
 * vocabulary is `RunRowState`; this one says where the run has got to at one
 * level of the tree.
 *
 * `running` a worker is on it now · `done` judged IN THIS RUN · `todo` in the
 * run's scope, not reached yet · `waiting` held by a question · `na` excluded ·
 * `null` nothing to say.
 */
export type RunDotState = 'running' | 'done' | 'todo' | 'waiting' | 'na' | null

/** The rows of ONE section of ONE adoption.
 *
 * The `adoption_id` half is load-bearing, not defensive: item rows stream for the
 * SELECTED adoption only, while the tree renders states for every EXPANDED one —
 * and `sub_key` is a plain chapter/clause pair, so ISO 13485's `4|4.1` and ISO
 * 14971's `4|4.1` are the same string. Filtering on `sub_key` alone showed one
 * referential's run state on another's tree (review catch, front#325).
 */
export function sectionRows(
  items: readonly AssessmentItemRow[],
  adoptionId: string,
  subKey: string,
): AssessmentItemRow[] {
  return items.filter((i) => i.adoption_id === adoptionId && i.sub_key === subKey)
}

/**
 * The state of one requirement within the live run whose entry is `entryId`.
 *
 * « Judged in this run » is READ, not folded (MYAC-1439): the engine stamps
 * each item row with the run entry that produced its current verdict
 * (`judged_run_id`), and the run row names its entry (`entry_id`). The old
 * rule compared the row's `updated_at` with a client-side run start and needed
 * a second guard because planning rewrites every row — both gone.
 */
export function itemRunState(
  row: AssessmentItemRow,
  opts: { entryId: string | null; runningCodes?: ReadonlySet<string> },
): RunDotState {
  if (!opts.entryId) return null
  if (row.status === 'not_applicable') return 'na'
  // MYAC-1424: a row waiting on the user's answer is neither reached nor skipped —
  // the run will not judge it until the question is answered.
  if (row.waiting_on) return 'waiting'
  const codes = opts.runningCodes
  if (codes && (codes.has(row.code) || codes.has(row.requirement_uid))) return 'running'
  return row.judged_run_id === opts.entryId ? 'done' : 'todo'
}

/**
 * Matryoshka: a parent is what its children are. Running wins over done, done over
 * todo — the most active state any child has.
 *
 * N/A is NEUTRAL: a section whose remaining requirements are all done IS done, since
 * the excluded ones were never going to move. Only when every child is excluded does
 * the parent read as excluded. Without that, one N/A holds a finished section at
 * "à venir" forever.
 */
export function foldRunStates(states: readonly RunDotState[]): RunDotState {
  const live = states.filter((s): s is Exclude<RunDotState, null> => s !== null)
  if (!live.length) return null
  if (live.includes('running')) return 'running'
  const counting = live.filter((s) => s !== 'na')
  if (!counting.length) return 'na'
  if (counting.every((s) => s === 'done')) return 'done'
  // Everything the run can reach is done; what remains waits on the user (MYAC-1424).
  if (counting.every((s) => s === 'done' || s === 'waiting')) return 'waiting'
  return 'todo'
}

/** The admission target a corpus-wide consistency sweep borrows (MYAC-1038):
 * the first adoption NOT currently running. The pick has no effect on the
 * sweep's scope — one pass covers the whole track — but admission refuses a
 * busy target, so a busy one must never be picked while an idle sibling
 * exists. `''` = every adoption busy (the launch button disables). */
export function sweepTargetAdoptionId(
  adoptionIds: readonly string[],
  busyAdoptions: Record<string, boolean>,
): string {
  return adoptionIds.find((aid) => !busyAdoptions[aid]) ?? ''
}

// ---------------------------------------------------------------------------
// Progression display (S3) — the pure half of what the header, the banner and
// the graph paint. Kept here, out of the store and the SFCs, because each of
// these rules is a tolerant-reader decision that deserves a test rather than a
// ternary buried in a template.
// ---------------------------------------------------------------------------

/**
 * Tolerant reader (spec §4.0-4): a phase this build has no label for degrades to
 * `unknown`, which renders the generic "working" line — never a raw engine word
 * leaking into the UI, never a missing translation key.
 *
 * Keyed on `PHASE_ORDER`, the single vocabulary (S3/6).
 */
export function normalizePhase(value: string | undefined | null): AssessmentPhase | null {
  if (!value) return null
  return (PHASE_ORDER as readonly string[]).includes(value) ? (value as AssessmentPhase) : 'unknown'
}

/**
 * Which kind of wait an `awaiting_human` row is in (S2 `await_kind`).
 *
 * The front had ONE surface for `awaiting_human` — the questions panel, falling
 * back to « L'analyse s'est interrompue » — so a clean budget yield was painted
 * as an incident, in the same banner as a real crash. This is the branch.
 *
 * Anything outside the closed vocabulary — `''` from a park an older server
 * could not classify, a member added later — reads as `incident`: the honest
 * catch-all. Never `relaunch`, which would promise an automatic resume nobody
 * guaranteed, and never `questions`, which would send the user to a panel that
 * owes them nothing.
 */
export function awaitKindOf(value: string | null | undefined): AwaitKind {
  if (value === 'questions') return 'questions'
  if (value === 'relaunch') return 'relaunch'
  return 'incident'
}

// ---------------------------------------------------------------------------
// The run row (MYAC-1433 / MYAC-1439) — ONE mapping table, the screen's one
// vocabulary for the track's run. The chip, the launch / stop controls, every
// re-assess trigger, the situation card and the popover read `run[track]`
// through this table and derive nothing else; the only wall-clock rule left
// on the page is `runLooksInterrupted`.
// ---------------------------------------------------------------------------

/** The eight states the engine projects, in no meaningful order. */
export const RUN_ROW_STATES: readonly RunRowState[] = [
  'idle',
  'starting',
  'running',
  'queued_behind_corpus',
  'waiting_human',
  'yielded',
  'aborted',
  'dead',
]

/** What one run state means ON SCREEN. */
export interface RunStateDisplay {
  /** The state's word, `referentials.witness.state.<key>` — the chip's label
   *  (composed with a count or a date where the state calls for one) and the
   *  popover's track line when no phase is named. */
  wordKey: string
  /** The engine's `live` facet for this state (`run.py` `_LIVE`) — used ONLY
   *  when the row carries no facet; the row's own facet always wins, so a
   *  state added tomorrow behaves right before this build knows its word. */
  live: boolean
  /** The engine's `needs_user` facet (`run.py` `_NEEDS_USER`) — same rule. */
  needsUser: boolean
  /** The chip's and the line's tone. */
  tone: 'rest' | 'busy' | 'attention'
  /** A spinner (the run is visibly working) — else `icon`. */
  spinning: boolean
  /** PrimeIcons class when not spinning. */
  icon: string
}

const W = 'referentials.witness.state.'

/** THE table. Tested row by row against the engine's facet sets. */
export const RUN_STATE_TABLE: Readonly<Record<RunRowState, RunStateDisplay>> = {
  idle: { wordKey: `${W}idle`, live: false, needsUser: false, tone: 'rest', spinning: false, icon: 'pi pi-clock' },
  starting: { wordKey: `${W}starting`, live: true, needsUser: false, tone: 'busy', spinning: true, icon: 'pi pi-clock' },
  running: { wordKey: `${W}running`, live: true, needsUser: false, tone: 'busy', spinning: true, icon: 'pi pi-clock' },
  // The corpus wait is a state like any other (MYAC-1439 gap 7): its word
  // comes from here, no front-invented « ingesting » pseudo-phase.
  queued_behind_corpus: { wordKey: `${W}queued_behind_corpus`, live: true, needsUser: false, tone: 'busy', spinning: false, icon: 'pi pi-hourglass' },
  waiting_human: { wordKey: `${W}waiting_human`, live: false, needsUser: true, tone: 'attention', spinning: false, icon: 'pi pi-question-circle' },
  // The platform's own handback: it relaunches by itself, nothing to do.
  yielded: { wordKey: `${W}yielded`, live: true, needsUser: false, tone: 'busy', spinning: false, icon: 'pi pi-refresh' },
  aborted: { wordKey: `${W}aborted`, live: false, needsUser: true, tone: 'attention', spinning: false, icon: 'pi pi-stop-circle' },
  dead: { wordKey: `${W}dead`, live: false, needsUser: true, tone: 'attention', spinning: false, icon: 'pi pi-exclamation-triangle' },
}

/**
 * The wire value → a state of the table. Absent / empty ⇒ `idle` (a track that
 * never ran on the engine that writes the row); a value this build does not
 * know ⇒ `running` (the enum's own `_missing_`: a newer value is a live one,
 * never a silent `idle` on a working engine).
 */
export function normalizeRunState(raw: unknown): RunRowState {
  if (typeof raw !== 'string' || raw === '') return 'idle'
  return (RUN_ROW_STATES as readonly string[]).includes(raw) ? (raw as RunRowState) : 'running'
}

/** The table's row for a wire value (see `normalizeRunState`). */
export function runStateDisplay(raw: unknown): RunStateDisplay {
  return RUN_STATE_TABLE[normalizeRunState(raw)]
}

/** Whose a queue item is: a referential's, the whole track's, or a kind this
 *  build does not know (read like a referential's — tolerant reader). */
export type RunQueueKind = 'referential' | 'track' | 'unknown'

/** One queued item, as the screen reads it. */
export interface RunQueueEntry {
  /** The referential it belongs to — `''` on a `track` item. */
  adoptionId: string
  commandId: string
  /** `owed` | `queued` | a newer value — informative, never branched on. */
  source: string
  kind: RunQueueKind
  /** The command a `track` item runs (`consistency`), `''` when unnamed. */
  type: string
}

function normalizeQueueKind(raw: unknown): RunQueueKind {
  if (raw === undefined || raw === null || raw === '' || raw === 'referential') return 'referential'
  return raw === 'track' ? 'track' : 'unknown'
}

/**
 * The `queue` field → entries: boxed items read as they are, a bare string
 * (an older projection) is boxed as a queued referential command with no id —
 * tolerant reader. `kind` absent ⇒ `referential`, an unknown value ⇒
 * `unknown` (attributed like a referential). A referential item is deduped by
 * adoption (first wins) and the head dropped: the head never waits behind
 * itself (design §6 rule 3). A `track` item (MYAC-1439: the corpus consistency
 * pass) is kept with its empty adoption, deduped by command (else by type).
 */
export function normalizeRunQueue(raw: unknown, head: string): RunQueueEntry[] {
  const out: RunQueueEntry[] = []
  const seen = new Set<string>(head ? [head] : [])
  const seenTrack = new Set<string>()
  for (const item of Array.isArray(raw) ? raw : []) {
    const boxed =
      typeof item === 'string'
        ? { adoption_id: item }
        : item && typeof item === 'object'
          ? (item as {
              adoption_id?: unknown
              command_id?: unknown
              source?: unknown
              kind?: unknown
              type?: unknown
            })
          : null
    if (!boxed) continue
    const kind = normalizeQueueKind(boxed.kind)
    const adoptionId = typeof boxed.adoption_id === 'string' ? boxed.adoption_id : ''
    const commandId = typeof boxed.command_id === 'string' ? boxed.command_id : ''
    const type = typeof boxed.type === 'string' ? boxed.type : ''
    if (kind === 'track') {
      const key = commandId || type
      if (seenTrack.has(key)) continue
      seenTrack.add(key)
    } else {
      if (!adoptionId || seen.has(adoptionId)) continue
      seen.add(adoptionId)
    }
    out.push({
      adoptionId: kind === 'track' ? '' : adoptionId,
      commandId,
      source: typeof boxed.source === 'string' && boxed.source ? boxed.source : 'queued',
      kind,
      type,
    })
  }
  return out
}

/** The queue's referential items — what positions, « N en attente » and the
 *  chip's « N restants » count. A `track` item belongs to no referential. */
export function runReferentialQueue(run: Pick<RunView, 'queue'>): RunQueueEntry[] {
  return run.queue.filter((q) => q.kind !== 'track')
}

/** 1-based position of a referential among the queue's referential items, 0
 *  when not queued. */
export function runQueuePosition(run: Pick<RunView, 'queue'>, adoptionId: string): number {
  if (!adoptionId) return 0
  return runReferentialQueue(run).findIndex((q) => q.adoptionId === adoptionId) + 1
}

/** Is a track command of this type waiting in the queue? */
export function runTrackCommandQueued(run: Pick<RunView, 'queue'>, type: string): boolean {
  return run.queue.some((q) => q.kind === 'track' && q.type === type)
}

/** The run row as the screen reads it — the two facets resolved, `Date`s. */
export interface RunView {
  state: RunRowState
  live: boolean
  needsUser: boolean
  since: Date | null
  commandId: string
  /** The adoption served now ('' when none — also while a TRACK command such
   *  as the consistency pass is served: no referential is « en cours » then). */
  head: string
  /** What the run serves next, in order — referential AND track items; read
   *  positions and counts through `runQueuePosition` / `runReferentialQueue`. */
  queue: RunQueueEntry[]
  /** The head's current step; `null` between steps and at rest. */
  phase: AssessmentPhase | null
  /** The CURRENT step's counters — both `null` when the step counts nothing. */
  done: number | null
  total: number | null
  lastStartedAt: Date | null
  /** The run's exit. */
  lastFinishedAt: Date | null
  /** Since when the track is assessed — the oldest referential's end in the last
   *  run (`last.assessed_at`), the exit when the row predates it. What « Évaluée
   *  il y a … » reads: a launch of two referentials is assessed since the FIRST
   *  one was done, not since the run's exit (Charles, 2026-09-23). */
  lastAssessedAt: Date | null
  lastOutcome: string
  heartbeatAt: Date | null
  /** The run entry's id — the dot's « judged in this run » key. */
  entryId: string
}

/** Firestore hands back a `Timestamp`; tests and cached reads may hand back a `Date`. */
function asDate(value: unknown): Date | null {
  if (value instanceof Date) return value
  const ts = value as { toDate?: () => Date } | null | undefined
  return typeof ts?.toDate === 'function' ? ts.toDate() : null
}

/**
 * Normalise one `run[track]` row — absent reads as idle, nothing else. The
 * facets come from the ROW when it carries them (the engine computes them), the
 * table only fills in for a row that has none.
 */
export function normalizeRunRow(raw: RunRow | null | undefined): RunView {
  const state = normalizeRunState(raw?.state)
  const entry = RUN_STATE_TABLE[state]
  const pt = raw?.progress?.total
  const total = typeof pt === 'number' && pt > 0 ? pt : null
  const pd = raw?.progress?.done
  const done = total !== null && typeof pd === 'number' ? Math.max(0, Math.min(pd, total)) : null
  const head = typeof raw?.head === 'string' ? raw.head : ''
  return {
    state,
    live: typeof raw?.live === 'boolean' ? raw.live : entry.live,
    needsUser: typeof raw?.needs_user === 'boolean' ? raw.needs_user : entry.needsUser,
    since: asDate(raw?.since),
    commandId: raw?.command_id ?? '',
    head,
    queue: normalizeRunQueue(raw?.queue, head),
    phase: normalizePhase(raw?.phase ?? null),
    done,
    total,
    lastStartedAt: asDate(raw?.last?.started_at),
    lastFinishedAt: asDate(raw?.last?.finished_at),
    lastAssessedAt: asDate(raw?.last?.assessed_at) ?? asDate(raw?.last?.finished_at),
    lastOutcome: raw?.last?.outcome ?? '',
    heartbeatAt: asDate(raw?.heartbeat_at),
    entryId: raw?.entry_id ?? '',
  }
}

/**
 * The track's run as the page reads it: `normalizeRunRow`, plus ONE
 * transitional fallback for the DATE only, never for the state.
 *
 * When `run[track]` is ABSENT from the status doc (a projection that predates
 * the run row — every environment until it has run on the new engine), the
 * track reads `idle`, its exit is the newest sealed `last_run.finished_at`
 * across the track's adoption rows and its « Évaluée il y a … » the OLDEST
 * (what is theirs: no timer, no state derivation). When the row is present,
 * its `last` is the only source, even if an adoption row carries another date.
 *
 * TRANSITIONAL: the fallback (the `adoptionRuns` argument) can go once every
 * environment (dev, staging, prod) has run on the engine that writes the row.
 */
export function trackRunView(
  raw: RunRow | null | undefined,
  adoptionRuns: ReadonlyArray<{ last_run?: { finished_at?: unknown } | null } | null | undefined>,
): RunView {
  const run = normalizeRunRow(raw)
  if (raw) return run
  let newest: Date | null = null
  let oldest: Date | null = null
  for (const a of adoptionRuns) {
    const at = asDate(a?.last_run?.finished_at)
    if (!at) continue
    if (newest === null || at > newest) newest = at
    if (oldest === null || at < oldest) oldest = at
  }
  return { ...run, lastFinishedAt: newest, lastAssessedAt: oldest }
}

/**
 * The referentials the track's LAST run served (MYAC-1278) — what the launch
 * modal pre-checks. Nothing records the user's last selection; the run does:
 * the engine seals each referential's window inside the run's own
 * [`last.started_at`, `last.finished_at`], the last one with the exit's very
 * clock, so the bounds are inclusive with no slack.
 *
 * No sealed run window (a projection that predates the run row, TRANSITIONAL
 * like `trackRunView`'s fallback) ⇒ the referentials sealed at the NEWEST end:
 * that engine stamped every referential of a run with the run's exit (observed
 * on dev, 2026-09-26: six referentials of one run, one end to the microsecond),
 * so an older, separate run's referential is left out.
 * Never assessed ⇒ empty: nothing is pre-checked.
 */
export function lastRunAdoptionIds(
  run: Pick<RunView, 'lastStartedAt' | 'lastFinishedAt'>,
  adoptions: ReadonlyArray<{ adoptionId: string; lastRun?: { finished_at?: unknown } | null }>,
): Set<string> {
  const sealed = adoptions.flatMap((a) => {
    const at = asDate(a.lastRun?.finished_at)
    return at ? [{ id: a.adoptionId, at: at.getTime() }] : []
  })
  const newest = Math.max(...sealed.map((s) => s.at))
  // `lastStartedAt` is null exactly when the run row carries no sealed window.
  const from = run.lastStartedAt?.getTime() ?? newest
  const to = run.lastStartedAt ? (run.lastFinishedAt?.getTime() ?? newest) : newest
  return new Set(sealed.filter((s) => s.at >= from && s.at <= to).map((s) => s.id))
}

/** The command doc fields `launchRequestSent` reads — `InflightIntent`'s shape. */
export interface OwnCommandLike {
  type: string
  status: string
  scopeTrack: string
  requestedBy: string
  queuedBehind: string
  assess: boolean
}

/**
 * « Demande envoyée… » (MYAC-1439): the browser deposited a launch-like command
 * on this track (reassess, resume, adopt-with-assess) and the handler has not
 * answered yet — `requested`, not queued behind another. A READ of a fact the
 * browser wrote, no timer: once admitted the engine writes `starting` and the
 * row wins (a live row always wins); a refusal leaves the in-flight stream,
 * its toast says why, and the chip goes back to the row's idle.
 */
export function launchRequestSent(
  run: Pick<RunView, 'live'>,
  commands: readonly OwnCommandLike[],
  track: string,
  ownUid: string | null | undefined,
): boolean {
  if (run.live || !ownUid) return false
  return commands.some(
    (c) =>
      c.requestedBy === ownUid &&
      c.scopeTrack === track &&
      c.status === 'requested' &&
      !c.queuedBehind &&
      (c.type === 'reassess' || c.type === 'resume' || (c.type === 'adopt' && c.assess)),
  )
}

/** Beyond this without a heartbeat a `running` display is more likely wrong
 *  than right (the measured longest healthy silence is heartbeat-covered). */
export const RUN_STALE_MS = 10 * 60_000

/**
 * « Interrompu ? » — the ONE wall-clock rule the page keeps: a dead job is the
 * one thing the engine cannot write about itself (the watchdog's `dead` state
 * is the durable answer; this is the safety net that only raises a doubt).
 * No heartbeat yet ⇒ no doubt.
 */
export function runLooksInterrupted(run: Pick<RunView, 'state' | 'heartbeatAt'>, nowMs: number): boolean {
  return run.state === 'running' && run.heartbeatAt !== null && nowMs - run.heartbeatAt.getTime() > RUN_STALE_MS
}

/** The situation card's copy (`referentials.questions.*`). */
export interface SituationCopy {
  titleKey: string
  detailKey: string
}

/**
 * The situation card — shown iff the run `needs_user`; its copy by state and
 * the head adoption's `await_kind`. A `questions` wait has no card of its own:
 * the question cards right above it ARE the ask.
 */
export function situationCopy(
  run: Pick<RunView, 'state' | 'needsUser'>,
  awaitKind: AwaitKind,
): SituationCopy | null {
  if (!run.needsUser) return null
  const Q = 'referentials.questions.'
  if (run.state === 'aborted') return { titleKey: `${Q}aborted`, detailKey: `${Q}abortedDetail` }
  if (run.state === 'waiting_human' && awaitKind === 'questions') return null
  if (run.state === 'waiting_human' && awaitKind === 'relaunch')
    return { titleKey: `${Q}pausedRelaunch`, detailKey: `${Q}pausedRelaunchDetail` }
  // A dead run, an incident park, and any state this build cannot name: the
  // honest catch-all, never a soothing one.
  return { titleKey: `${Q}paused`, detailKey: `${Q}pausedDetail` }
}

/** The row steps the popover's dot chain renders, in pipeline order. */
export const ROW_STEPS: readonly AssessmentPhase[] = [
  'preparing', 'planning', 'assessing', 'verifying', 'tasks', 'summarizing',
]

/**
 * «{verb} : {label}» — the first unit genuinely under a worker right now.
 *
 * `detail` is an i18n key SUFFIX chosen by the SERVER, so this build may not
 * know it: guarded with `te()` (the `RunQueuePill` idiom), because the one thing
 * that must never reach the screen is `referentials.progress.unit.some_new_kind`.
 * An unrecognised detail falls back to the bare label — the label is already
 * client-facing vocabulary (a filename, a clause code) and says most of it.
 * Scope-level units may carry no label at all: the verb is then the whole
 * message, and an unknown verb with no label means there is nothing to say.
 */
export function unitTickerText(
  units: readonly ActiveUnit[] | null | undefined,
  t: (key: string) => string,
  te: (key: string) => boolean,
): string {
  const unit = units?.[0]
  if (!unit) return ''
  // ONE copy for the EUDAMED refresh everywhere (Charles, 19/08): the row's
  // dedicated sub-text and this ticker rendered two different texts for the
  // same unit («Mise à jour des informations EUDAMED…» vs a bare «EUDAMED»).
  if (unit.kind === 'registry') return t('referentials.progress.registryRefresh')
  // Same rule for comprehension: a label-less analysis unit has nothing more
  // precise than its nature — one sentence, identical to the row's sub-text.
  if (unit.kind === 'analysis' && !unit.label)
    return t('referentials.progress.understanding')
  const key = unit.detail ? `referentials.progress.unit.${unit.detail}` : ''
  const verb = key && te(key) ? t(key) : ''
  if (!unit.label) return verb
  return verb ? `${verb} : ${unit.label}` : unit.label
}

/**
 * The in-phase fraction, alone (« 4/51 »).
 *
 * The batch position (« lot 2/11 ») is ENGINE-INTERNAL operator telemetry and
 * must never reach a client's screen (Charles, 2026-08-18 — spotted on the
 * very first painted run). The projection keeps carrying `batch`; the
 * parameters stay so call sites don't churn, but the string never includes it.
 *
 * Still no fabricated fraction: a missing or zero denominator yields ''.
 */
export function fractionWithBatch(
  done: number | null | undefined,
  total: number | null | undefined,
  // The batch ("lot n/m") is ENGINE-INTERNAL and must never reach a client's
  // screen (Charles, 2026-08-18 — spotted live on the very first painted run).
  // The projection keeps carrying it for operators; the parameters stay so no
  // call site churns, but the string is the fraction alone.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _batch: string | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _batchLabel: (batch: string) => string,
): string {
  if (typeof total === 'number' && total > 0 && typeof done === 'number')
    return `${done}/${total}`
  return ''
}



/**
 * Settle ONLY when the launch's own submission settles (décision Charles
 * 19/08, third live recurrence of the batch-ordering bug): `guardedLaunch`
 * used to FIRE the launch and resolve at once, so the launch modal's
 * «sequential» batch degenerated into a POST race and the rail's FIFO
 * honored the browser's arrival order, not the modal's.
 */
export function awaitLaunch(launch: () => void | Promise<unknown>): Promise<void> {
  return Promise.resolve(launch()).then(() => undefined)
}

/** The `t` shape `renderHistoryNote` needs — vue-i18n's `(key, named, plural)`. */
export type NoteTranslate = (
  key: string,
  named?: Record<string, unknown>,
  count?: number,
) => string

/**
 * The history line's closing text (décision Charles 19/08): ONE main segment +
 * AT MOST one qualifier, joined by « · » — ~60 characters worst case, so the
 * modal line never truncates. The engine sends a closed {key, counts} note;
 * the audit rationale is never displayed. Unknown key or nothing to say → ''
 * (the line shows label + duration only).
 */
export function renderHistoryNote(note: HistoryNote | undefined, t: NoteTranslate): string {
  if (!note?.key) return ''
  const n = (field: string): number => Math.max(0, Number(note[field] ?? 0) || 0)
  const K = 'referentials.graph.note.'
  const seg = (key: string, count: number, named: Record<string, unknown> = {}): string =>
    t(K + key, { n: count, ...named }, count)
  const parts: string[] = []
  switch (note.key) {
    case 'planned':
      if (n('to_assess') > 0) parts.push(seg('planned', n('to_assess')))
      if (n('na') > 0) parts.push(seg('plannedNa', n('na')))
      break
    case 'read':
      if (n('read') === 0 && n('failed') === 0)
        return n('unchanged') > 0 ? t(K + 'readNone') : ''
      parts.push(seg('read', n('read')))
      if (n('failed') > 0) parts.push(seg('readFailed', n('failed')))
      break
    case 'assessed':
      parts.push(seg('assessed', n('judged')))
      if (n('up_to_date') > 0) parts.push(seg('assessedUpToDate', n('up_to_date')))
      break
    case 'assessed_interrupted':
      return seg('assessedInterrupted', n('judged'), { m: n('total') })
    case 'verify_interrupted':
      return seg('verifiedInterrupted', n('verified'))
    case 'verified': {
      if (n('verified') === 0 && n('requeued') === 0) return ''
      // «5 constats vérifiés (3 documents)» — the examined carrier documents
      // ride the main segment in parentheses (Charles, 19/08).
      const docs = n('documents')
      parts.push(
        seg('verified', n('verified')) + (docs > 0 ? ' ' + seg('verifiedDocs', docs) : ''),
      )
      // Priority: the send-back explains the Évaluation line that follows —
      // it beats the correction count when both apply.
      if (n('requeued') > 0) parts.push(seg('verifiedRequeued', n('requeued')))
      else if (n('corrected') > 0) parts.push(seg('verifiedCorrected', n('corrected')))
      break
    }
    case 'consistency':
      if (n('checked') === 0) return ''
      parts.push(seg('consistency', n('checked')))
      if (n('gaps') > 0) parts.push(seg('consistencyGaps', n('gaps')))
      break
    case 'tasks': {
      const created = n('created')
      const updated = n('updated')
      const resolved = n('resolved')
      if (created > 0) {
        parts.push(seg('tasksCreated', created))
        if (Math.max(updated, resolved) > 0)
          parts.push(
            updated >= resolved ? seg('tasksUpdated', updated) : seg('tasksResolved', resolved),
          )
      } else if (updated > 0) parts.push(seg('tasksUpdatedAlone', updated))
      else if (resolved > 0) parts.push(seg('tasksResolvedAlone', resolved))
      else return t(K + 'tasksUnchanged')
      break
    }
    case 'summarized':
      return t(K + 'summarized')
    // The two marks with a label and no counts (MYAC-1439 gap 5): their strings
    // were authored and never rendered once the graph that overrode the label
    // was retired.
    case 'resumed':
      return t(K + 'resumedLabel')
    case 'answered':
      return t(K + 'answeredLabel')
    default:
      return ''
  }
  return parts.filter(Boolean).join(' · ')
}

/**
 * The action-plan tab badge: OPEN TASK ROWS on the track, rejected excluded
 * (`open_rows`, §12.20) — the number a user can reconcile with the rows in front
 * of them. Not `open_actions`: that is distinct CLAUSES, and a badge that did not
 * move when one of two tasks on a shared requirement was rejected read as a bug
 * (MYAC-1032 v3, 2026-09-03). Still a materialized read, never a reduce over the
 * lazily loaded rows (spec §4.0-1).
 */
export function actionPlanBadgeCount(
  taskGroups: AssessmentTaskGroupsView | null | undefined,
  track: ScopeTrack,
): number {
  return taskGroups?.[track]?.open_rows ?? 0
}
